import {
  SignedRecordJsonObject,
  SignedRecordJsonValue,
  SignedSafetyControlRecord,
} from '../../services/reportsApi';

export const SIGNED_RECORD_UNAVAILABLE = 'Not available on this signed revision.';

/** Titles longer than this are treated as narrative, not page titles. */
export const SIGNED_RECORD_TITLE_MAX_CHARS = 72;

export type SignedRecordSectionId =
  | 'summary'
  | 'finding'
  | 'control'
  | 'recheck'
  | 'evidence'
  | 'governance'
  | 'technical-annex';

export interface SignedRecordContentGroup {
  label: string;
  value: SignedRecordJsonValue;
}

export interface SignedRecordSectionModel {
  id: SignedRecordSectionId;
  title: string;
  available: boolean;
  groups: SignedRecordContentGroup[];
}

function toSignedJson(value: unknown): SignedRecordJsonValue | undefined {
  if (value === null || typeof value === 'string' || typeof value === 'number'
      || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map(toSignedJson)
      .filter((item): item is SignedRecordJsonValue => item !== undefined);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce<SignedRecordJsonObject>((result, [key, item]) => {
      const normalized = toSignedJson(item);
      if (normalized !== undefined) result[key] = normalized;
      return result;
    }, {});
  }
  return undefined;
}

export function hasSignedRecordValue(value: SignedRecordJsonValue | undefined): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasSignedRecordValue);
  if (typeof value === 'object') return Object.values(value).some(hasSignedRecordValue);
  return true;
}

function group(label: string, value: unknown): SignedRecordContentGroup | null {
  const normalized = toSignedJson(value);
  return normalized !== undefined && hasSignedRecordValue(normalized)
    ? { label, value: normalized }
    : null;
}

function section(
  id: SignedRecordSectionId,
  title: string,
  entries: Array<SignedRecordContentGroup | null>,
): SignedRecordSectionModel {
  const groups = entries.filter((entry): entry is SignedRecordContentGroup => entry !== null);
  return { id, title, available: groups.length > 0, groups };
}

export function resolveSignedRecordPageTitle(record: SignedSafetyControlRecord): string {
  return record.signed_body.identity.title || 'Signed Safety Control Record';
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

/**
 * Artifact routes are keyed by Safety Scan report id, which can differ from
 * the signed-record route param (`record_id`).
 */
export function resolveSignedRecordLineageLabels(
  record: SignedSafetyControlRecord | null | undefined,
): {
  caseLabel: string;
  scanLabel: string;
  recordLabel: string;
  hasManagedCase: boolean;
} {
  const title = firstText(
    record?.signed_body?.identity?.title,
    record?.identity?.condition_label,
  );
  const hasManagedCase = Boolean(firstText(record?.related?.safety_case_id));
  const windowLabel = firstText(record?.signed_body?.scope?.observation_window_label, 'Safety Scan');
  const revision = record?.revision;
  return {
    caseLabel: hasManagedCase
      ? (title ? `Safety Case · ${title}` : 'Safety Case')
      : 'Managed Safety Case: Not created',
    scanLabel: windowLabel.startsWith('Safety Scan') ? windowLabel : `Safety Scan · ${windowLabel}`,
    recordLabel: revision != null ? `Signed Record · Rev ${revision}` : 'Signed Record',
    hasManagedCase,
  };
}

/**
 * Artifact routes are keyed by Safety Scan report id, which can differ from
 * the signed-record route param (`record_id`).
 */
export function resolveSignedRecordArtifactReportId(
  record: SignedSafetyControlRecord | null | undefined,
  fallbackRecordId?: string | null,
): string {
  const scan = asObject(record?.detail?.scan_report);
  const scanIdentity = asObject(scan.identity);
  const document = asObject(record?.detail?.document_control);
  const annex = asObject(record?.signed_body?.technical_annex);
  const provenance = asObject(annex.provenance);
  return firstText(
    scanIdentity.report_id,
    scan.report_id,
    document.report_id,
    document.document_id,
    provenance.report_id,
    provenance.source_report_id,
    fallbackRecordId,
    record?.record_id,
  );
}

/**
 * Copies the canonical API DTO into native sections. It never reads legacy detail/scan aliases
 * and never derives rates, percentages, comparability, result direction, coverage, or claimability.
 */
export function normalizeSignedControlRecord(
  record: SignedSafetyControlRecord,
): SignedRecordSectionModel[] {
  const body = record.signed_body;
  const annexAvailable = body.technical_annex.available
    && body.technical_annex.source_revision_matches;

  return [
    section('summary', 'Summary', [
      group('Signed result', body.result),
      group('Operating decision', body.next_decision),
    ]),
    section('finding', 'Finding', [
      group('Signed finding', body.finding),
    ]),
    section('control', 'Control', [
      group('Signed control', body.control),
    ]),
    section('recheck', 'Recheck', [
      group('Signed recheck', body.recheck),
    ]),
    section('evidence', 'Evidence', [
      group('Signed evidence', body.evidence),
    ]),
    section('governance', 'Governance', [
      group('Identity', body.identity),
      group('Governance', body.governance),
      group('Scope', body.scope),
    ]),
    section('technical-annex', 'Technical annex', [
      annexAvailable
        ? group('Matching-revision technical annex', body.technical_annex)
        : null,
    ]),
  ];
}
